import { useEffect, useRef, useState } from 'react'

const SETTLE_DELAY_MS = 700

/** Follows a value that changes on every keystroke, skipping the half-typed states. */
export function useSettledValue<T>(value: T, delayMs = SETTLE_DELAY_MS): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSettled(value), delayMs)
    return () => window.clearTimeout(timeoutId)
  }, [delayMs, value])

  return settled
}

/** Runs once the user stops changing the value. The initial value does not count. */
export function useOnSettled<T>(value: T, onSettled: (value: T) => void, delayMs = SETTLE_DELAY_MS): void {
  const callbackRef = useRef(onSettled)
  callbackRef.current = onSettled
  const initialValueRef = useRef(value)

  useEffect(() => {
    if (Object.is(value, initialValueRef.current)) return
    initialValueRef.current = undefined as T
    const timeoutId = window.setTimeout(() => callbackRef.current(value), delayMs)
    return () => window.clearTimeout(timeoutId)
  }, [delayMs, value])
}
